import { ClickAwayListener, FormControl, makeStyles } from '@material-ui/core'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Moment } from 'moment'
import moment from 'moment'
import type { PopperProps as MuiPopperProps } from '@material-ui/core/Popper'
import ClearIcon from '@material-ui/icons/Clear'
import classnames from 'classnames'
import type { BreakpointValues } from '@material-ui/core/styles/createBreakpoints'
import type { CalendarEvent } from 'paintscout'
import IconButton from '@paintscout/ui/components/IconButton'
import Paper from '@paintscout/ui/components/Paper'
import ResponsivePopper from '@paintscout/ui/components/ResponsivePopper'
import InputField from '@paintscout/ui/components/InputField'
import Button from '@paintscout/ui/components/Button'
import { useBreakpoint } from '@paintscout/ui/hooks'
import { useTranslation } from '@paintscout/ui'
import { useDatePickerStyles } from './useDatePickerStyles'
import SingleDatePickerController from './SingleDatePickerController'

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    margin: theme.spacing(4),
    maxWidth: '450px',
    minWidth: '450px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      maxWidth: '100%'
    }
  },
  dialogContent: {},
  disabledInput: {
    cursor: 'default !important',
    '& input': {
      cursor: 'default !important'
    }
  }
}))

export interface SingleDatePickerProps {
  classes?: Partial<ReturnType<typeof useStyles>>
  label?: string
  sublabel?: string
  date?: Moment
  disabled?: boolean
  PopperProps?: Partial<MuiPopperProps>
  modalBreakpoint?: keyof BreakpointValues
  clearable?: boolean
  customTrigger?: React.ReactNode
  events?: CalendarEvent[]
  onChange: (date: Moment) => void
  required?: boolean
}

export function SingleDatePicker(props: SingleDatePickerProps) {
  const localClasses = useStyles(props)
  const { __ } = useTranslation()
  const {
    label,
    sublabel,
    disabled,
    clearable,
    customTrigger,
    PopperProps,
    modalBreakpoint = 'sm',
    onChange,
    events = [],
    required = false
  } = props
  const mobile = useBreakpoint((breakpoints) => breakpoints.down(modalBreakpoint))
  const dateFormat = 'MMM D, YYYY'

  const [tableHeight, setTableHeight] = useState(0)

  const anchorRef = useRef(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [date, setDate] = useState<Moment>(props.date)

  useEffect(() => {
    setDate(props.date)
  }, [props.date])

  // useLayoutEffect runs after DOM mutation but before paint, so the measured
  // height is applied before the user sees the fallback (avoids a flicker).
  useLayoutEffect(() => {
    recalculateTableHeight()
  }, [anchorEl])

  const recalculateTableHeight = () => {
    // Scope the query to this picker's subtree so concurrent SingleDatePicker
    // instances (e.g. one inside a dialog stacked over the page) don't measure
    // each other's months. Skip hidden adjacent months (forced to height: 0 by
    // useDatePickerStyles) so we read the actual visible month instead of 0.
    const root = paperRef.current
    if (!root) return
    const visible = root.querySelector<HTMLElement>(
      '.CalendarMonthGrid_month__horizontal:not(.CalendarMonthGrid_month__hidden)'
    )
    if (visible && visible.offsetHeight > 0) {
      setTableHeight(visible.offsetHeight)
    }
  }

  const classes = useDatePickerStyles({ isSingle: true, tableHeight })

  const handleClickAway = (e: React.MouseEvent<Document, MouseEvent>) => {
    if (e.target instanceof HTMLElement && e.target.className.includes('top-navigation')) {
      // This means the click was in a nested dropdown, so we don't want to close the date picker
      return
    }
    setAnchorEl(null)
  }

  const handleChange = (date: Moment) => {
    setDate(date)
    setAnchorEl(null)
    onChange(date)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    setDate(null)
    onChange(null)
    setAnchorEl(null)
  }

  const handleTriggerClick = () => {
    setAnchorEl(anchorEl ? null : anchorRef.current)
  }

  const getEventsForDay = (day: Moment) => {
    return events.filter((event) => {
      const start = moment(event.start).startOf('day')
      const end = moment(event.end ?? event.start).endOf('day')
      return day.isBetween(start, end, 'day', '[]')
    })
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FormControl fullWidth>
        {customTrigger ? (
          React.cloneElement(customTrigger as React.ReactElement, {
            onClick: handleTriggerClick,
            ref: anchorRef
          })
        ) : (
          <InputField
            required={required}
            className={classnames(classes.input, { [localClasses.disabledInput]: disabled })}
            placeholder={__('Select Date')}
            inputRef={anchorRef}
            readOnly
            fullWidth
            value={date ? date.format(dateFormat) : ''}
            label={label}
            sublabel={sublabel}
            onClick={handleTriggerClick}
            disabled={disabled}
            endAdornment={
              clearable &&
              date && (
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              )
            }
          />
        )}
        <ResponsivePopper
          classes={{
            root: classes.popper,
            dialogRoot: classnames(classes.popper, localClasses.dialogRoot),
            dialogContent: classnames(classes.dialogContent, localClasses.dialogContent)
          }}
          open={!!anchorEl}
          PopperProps={{
            anchorEl,
            placement: 'bottom-start',
            modifiers: {
              flip: {
                enabled: false
              }
            },
            disablePortal: true,
            ...PopperProps
          }}
          modalBreakpoint={modalBreakpoint}
          footer={
            mobile && (
              <div className={classes.mobileFooter}>
                <Button variant="textNoPadding" onClick={() => setAnchorEl(null)}>
                  {__('Cancel')}
                </Button>
              </div>
            )
          }
        >
          <div ref={paperRef}>
            <Paper className={classes.paper}>
              <SingleDatePickerController
                date={date}
                onChange={handleChange}
                getEventsForDay={getEventsForDay}
                recalculateTableHeight={recalculateTableHeight}
              />
            </Paper>
          </div>
        </ResponsivePopper>
      </FormControl>
    </ClickAwayListener>
  )
}
